import {
  matchMemberIds,
  nameOfSide,
  sideIds,
  sideOwning,
} from '@polf/shared/match'

import {
  type CurrentPlayerFlightMatchesQuery,
  type FlightPlayerScoresFragment,
  HoleWinnerEnum,
  MatchSideEnum,
  MatchStatusEnum,
} from '../../../__generated__/hooks'
import { groupByTeam } from '../../../util/best-ball'
import {
  decidedMargin,
  holeOutcome,
  LEG_LABELS,
  playOrderNumbers,
} from '../../../util/match-progression'
import PlayerScoreCard from '../components/PlayerScoreCard'

type Match =
  CurrentPlayerFlightMatchesQuery['currentPlayerFlightMatches'][number]

/**
 * Match-grouped score entry. Each pairing is its own block, so who is playing
 * whom is visible from the shape of the screen rather than inferred from a
 * separate band — and the standing sits in the block it belongs to.
 *
 * Every match in the flight is shown, not just the key-holder's: one person
 * normally enters for the whole flight, so the other pairing has to be
 * readable to the person entering its scores.
 *
 * design.md §6.5b.
 */
const MatchLayout = ({
  flight,
  matches,
  currentHole,
  courseHoles,
  startHole,
  block,
  showChips,
  isNet,
  isYourEntry,
  onSetScore,
  saving,
}: {
  flight: FlightPlayerScoresFragment[]
  matches: Match[]
  currentHole: number
  /**
   * The block these matches are played over, on a split round. Scopes the
   * play order, because a block's match knows only its own six holes.
   */
  block?: { fromHole: number; toHole: number } | null
  /** Needed to say WHICH hole a result belongs to — see `playOrderNumbers`. */
  courseHoles: readonly { number: number }[]
  startHole?: number | null
  showChips: boolean
  isNet: boolean
  isYourEntry: (entryId: string) => boolean
  onSetScore: (scoreId: string, gross: number) => void
  saving: boolean
}) => {
  // state.holes is indexed by the holes the MATCH is played over, which on a
  // split round is its block's six rather than the whole card. Handing the
  // full card here would shift every per-hole readout by the blocks before it.
  const order = playOrderNumbers(
    block
      ? courseHoles.filter(
          (h) => h.number >= block.fromHole && h.number <= block.toHole
        )
      : courseHoles,
    block ? null : startHole
  )
  // Which ball counted for each team on the visible hole. Reuses best ball's
  // own resolution rather than a second copy of it — the same function the
  // non-match best-ball layout uses.
  const contributorByTeam = new Map(
    groupByTeam(flight, currentHole).map((group) => [
      group.teamId,
      group.bestForHole.contributorId,
    ])
  )
  // Both axes: a one-ball team entry IS the side, a best-ball entry is one of
  // its members. Either way the entry is in a match and gets no lonely card.
  const inAMatch = new Set(
    matches.flatMap((m) => [...sideIds(m), ...matchMemberIds(m)])
  )
  // An odd field leaves someone without an opponent. They still play the round
  // and still count on the stroke leaderboard, so their card must still be here.
  const unpaired = flight.filter((e) => !inAMatch.has(e.id))

  return (
    <div className="flex flex-col gap-3 px-2 mt-3">
      {matches.map((match) => {
        const { state } = match
        const nameFor = (side: MatchSideEnum) => nameOfSide(match, side)

        const decided = state?.status === MatchStatusEnum.Decided
        const halved = state?.status === MatchStatusEnum.Halved
        const dormie = state?.status === MatchStatusEnum.Dormie

        let standing = 'All square'
        let tone = 'text-ink-700'
        if (decided && state) {
          // Gold is spent here and nowhere else in a match.
          standing = `${nameFor(
            state.leader ?? MatchSideEnum.A
          )} vant ${decidedMargin(state)}`.trim()
          tone = 'text-gold'
        } else if (halved) {
          standing = 'Matchen ble delt'
        } else if (state?.leader && state.holesUp > 0) {
          standing = `${nameFor(state.leader)} ${state.holesUp} UP`
          tone = 'text-green-700'
        }

        // Who took the hole in front of the player. The card wears it as a
        // ring; a halved hole has no card to mark, so the header says so.
        const outcome = state ? holeOutcome(state, order, currentHole) : null
        // Best Worst: both balls always count, so no card is "the one that
        // counted" — the header names each leg's winner instead.
        const legged = (state?.pointsPerHole ?? 1) > 1
        const legName = (winner: HoleWinnerEnum) =>
          winner === HoleWinnerEnum.Halved
            ? 'delt'
            : nameFor(
                winner === HoleWinnerEnum.A ? MatchSideEnum.A : MatchSideEnum.B
              )
        const wonBy = (id?: string | null) => {
          if (!outcome || outcome.winner === HoleWinnerEnum.Halved) return false
          const owner = sideOwning(match, id)
          if (!owner) return false
          return outcome.winner === HoleWinnerEnum.A
            ? owner === 'A'
            : owner === 'B'
        }

        // Both axes, because a side is not always a flight entry: in a
        // one-ball format the entry IS the team, while in best ball the
        // entries are its members. `side.id` is the id the score rows are
        // keyed by, so one predicate covers all three shapes.
        const entriesForSide = (side?: { id: string } | null) =>
          side
            ? flight.filter((e) => e.id === side.id || e.team?.id === side.id)
            : []

        const sideGroups = (
          [
            [MatchSideEnum.A, match.sideA],
            [MatchSideEnum.B, match.sideB],
          ] as const
        ).map(([key, side]) => ({
          key,
          side,
          entries: entriesForSide(side),
        }))

        // A best-ball side is several cards, so the ring that says "this side
        // took the hole" moves to the group. On a one-card side it stays on
        // the card, which keeps singles and one-ball team blocks rendering
        // exactly as they did.
        const grouped = sideGroups.some((g) => g.entries.length > 1)

        return (
          <section
            key={match.id}
            className="flex flex-col gap-2 p-2 bg-ink-100 rounded-lg"
            aria-label={`Match ${match.matchNumber}`}
          >
            <header className="flex items-baseline justify-between gap-2 px-2 pt-1">
              <span className="flex items-baseline gap-2 min-w-0">
                <span className={`font-display font-extrabold text-sm ${tone}`}>
                  {standing}
                </span>
                {!legged && outcome?.winner === HoleWinnerEnum.Halved ? (
                  <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-ink-500">
                    Hull {currentHole} delt
                  </span>
                ) : null}
              </span>
              {!decided && !halved && state ? (
                <span className="text-xs font-mono num text-ink-500 shrink-0">
                  {dormie ? 'Dormie · ' : ''}
                  {state.holesRemaining} hull igjen
                </span>
              ) : null}
            </header>
            {legged && outcome ? (
              <p className="px-2 -mt-1 text-xs text-ink-700">
                Hull {currentHole}:{' '}
                {outcome.legs
                  .map((w, i) => `${LEG_LABELS[i]} ${legName(w)}`)
                  .join(' · ')}
                {outcome.capped ? ' — lederen får maks ett poeng' : ''}
              </p>
            ) : null}

            {sideGroups.map(({ key, side, entries }) => {
              const cards = entries.map((entry) => (
                <PlayerScoreCard
                  key={entry.id}
                  entry={entry}
                  holeNumber={currentHole}
                  isYou={isYourEntry(entry.id)}
                  showChips={showChips}
                  isNet={isNet}
                  usedByTeam={false}
                  countsForSide={
                    grouped &&
                    !legged &&
                    !!side &&
                    contributorByTeam.get(side.id) === entry.id
                  }
                  /**
                   * The side's differential — one of the two is always 0,
                   * since the lower handicap plays off scratch.
                   *
                   * Best ball has no side allowance (`handicap` is null), and
                   * its differential is a real 0 rather than a missing value,
                   * so `?? ` does not fall through it. Branch on the handicap
                   * instead and hand the card the player's OWN strokes, which
                   * is what they actually play off.
                   */
                  matchStrokes={
                    side?.handicap != null ? side.strokes : entry.strokes
                  }
                  wonHole={!grouped && wonBy(entry.id)}
                  onSetScore={onSetScore}
                  saving={saving}
                />
              ))

              if (!grouped) return cards

              return (
                <div
                  key={key}
                  className={`flex flex-col gap-2 p-2 rounded-lg ${
                    wonBy(side?.id) ? 'ring-2 ring-green-700' : ''
                  }`}
                >
                  <span className="px-1 text-[10px] font-bold uppercase tracking-wider text-ink-500 truncate">
                    Lag · {side?.name}
                  </span>
                  {cards}
                </div>
              )
            })}
          </section>
        )
      })}

      {unpaired.length > 0 && (
        <section
          className="flex flex-col gap-2 p-2 bg-ink-100 rounded-lg"
          aria-label="Uten motstander"
        >
          <header className="px-2 pt-1 text-xs font-bold uppercase tracking-caps text-ink-500">
            Uten motstander
          </header>
          {unpaired.map((entry) => (
            <PlayerScoreCard
              key={entry.id}
              entry={entry}
              holeNumber={currentHole}
              isYou={isYourEntry(entry.id)}
              showChips={showChips}
              isNet={isNet}
              usedByTeam={false}
              onSetScore={onSetScore}
              saving={saving}
            />
          ))}
        </section>
      )}
    </div>
  )
}

export default MatchLayout
